import React, { ComponentProps, useCallback, useEffect } from 'react';
import { useInView } from 'react-hook-inview';

import { useLocation } from '@reach/router';
import {
  IconCpu,
  IconFilter,
  IconShare,
  IconUserScan,
} from '@tabler/icons-react';

import { BottomCta } from '../../components/BottomCta';
import * as bottomCta from '../../components/BottomCta/SuccessPredictorBackground.module.css';
import Footer from '../../components/Footer';
import { HeroProduct } from '../../components/Heros';
import { Section } from '../../components/Section';
import Seo from '../../components/Seo';
import Header from '../../components/navbar/Header';
import MobileNav from '../../components/navbar/MobileNav';
import { LottieAnimation } from '../../components/web/lottieAnimation';
import { useMixpanel } from '../../functions/Mixpanel';
import HowItWorksAnimation from '../../images/SuccessPredictor/howItWorksAnimation.json';
import productImg from '../../images/SuccessPredictor/spProductImage.webp';
import heroImg from '../../images/SuccessPredictor/successPredictorHero.webp';
import spIcon from '../../images/productImages/successPredictor.svg';

const LottieProps: ComponentProps<typeof LottieAnimation>['desktopOptions'] = {
  loop: false,
  autoplay: false,
  animationData: HowItWorksAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const SuccessPredictor = () => {
  const { mixpanel } = useMixpanel();
  const location = useLocation();

  const requestDemoTrack = useCallback(
    (section: string) => {
      mixpanel.track('Request Demo', {
        'Page Name': 'SP Assist Landing Page',
        Section: section,
      });
    },
    [mixpanel],
  );

  const [ref, inView] = useInView({ threshold: 0.3 });

  useEffect(() => {
    mixpanel.track('View Page', { 'Page Name': 'SP Assist Landing Page' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Header
        pageName="SP Assist Landing Page"
        pageCategory={undefined}
        link="/success-predictor/request-demo"
      />
      <MobileNav
        buttonTitle={undefined}
        link="/success-predictor/request-demo"
        openInNewTab={undefined}
      />
      <Seo
        title="Success Predictor: Elevate IVF Patient Consultations"
        description="Discover how Success Predictor leverages AI to create personalized IVF reports, helping clinicians set realistic expectations and boost patient confidence in treatment decisions."
        siteUrl={location.href}
        keywords={undefined}
        image={undefined}
      />
      <div className="tw-flex tw-flex-col tw-items-center">
        <div className={classNames.heroContainer}>
          <HeroProduct
            className={classNames.hero}
            product="Success Predictor"
            productImg={spIcon}
            title="Elevate your IVF consultations"
            subtitle="Success Predictor leverages AI to create personalized reports for each patient, helping you set realistic expectations and boosting patient confidence in their treatment decisions."
            ctaText="Request a demo"
            ctaLink="/success-predictor/request-demo"
            ctaCallback={() => requestDemoTrack('SP Landing Page Header')}
            ctaDescription="Book a 20-minute personalized demo and get set up with a free trial account. No strings attached."
          >
            <div className={classNames.heroChildren}>
              <img className={classNames.heroImage} src={heroImg} />
            </div>
          </HeroProduct>
        </div>
        <Section className={classNames.howItWorks} bgClassName="tw-bg-space">
          <h4
            className={[
              'lg:tw-hidden tw-flex tw-self-start',
              classNames.howItWorksEyebrow,
            ].join(' ')}
          >
            How it works
          </h4>
          <div
            ref={ref}
            className={[
              'tw-max-w-[608px]',
              classNames.howItWorksContainer,
            ].join(' ')}
          >
            <LottieAnimation
              inView={inView}
              mobileOptions={LottieProps}
              desktopOptions={LottieProps}
            />
          </div>
          <div className={classNames.howItWorksContainer}>
            <h4
              className={[
                'tw-hidden lg:tw-flex ',
                classNames.howItWorksEyebrow,
              ].join(' ')}
            >
              How it works
            </h4>
            <h3 className={classNames.howItWorksTitle}>
              Getting started is easy, no EMR integration required
            </h3>
            <ul className="tw-list-disc">
              <li className={classNames.howItWorksBullet}>
                Simply input patient data to generate a report instantly during
                your consultations.
              </li>
              <li className={classNames.howItWorksBullet}>
                Share reports easily to engage with and empower patients as they
                navigate the next step of their treatment plan.
              </li>
            </ul>
          </div>
        </Section>
        <Section className={classNames.productSection}>
          <div className={classNames.productTitle}>
            <h3 className="tw-display2-teodor">
              Help patients make informed decisions, faster
            </h3>
            <h3 className="tw-h3">
              When patients are met with clear options and education about the
              IVF journey, they can confidently proceed with their treatment,
              build trust with their care team, and feel satisfied with their
              end results.
            </h3>
          </div>
          <div className={classNames.productContentContainer}>
            <div className={classNames.productContent}>
              <div className={classNames.productItem}>
                <div className={classNames.productIconContainer}>
                  <IconShare className={classNames.productIcon} />
                  <h3 className={classNames.productItemTitle}>
                    Share seamlessly with patients
                  </h3>
                </div>
                <p className={classNames.productItemDescription}>
                  Give patients access to their report before or after your
                  consult to keep them engaged.
                </p>
              </div>
              <div className={classNames.productItem}>
                <div className={classNames.productIconContainer}>
                  <IconFilter className={classNames.productIcon} />
                  <h3 className={classNames.productItemTitle}>
                    Personalized IVF funnel
                  </h3>
                </div>
                <p className={classNames.productItemDescription}>
                  Educate patients about attrition with predictions for each
                  stage of their cycle.
                </p>
              </div>
              <div className={classNames.productItem}>
                <div className={classNames.productIconContainer}>
                  <IconCpu className={classNames.productIcon} />
                  <h3 className={classNames.productItemTitle}>
                    Predicted chance of reaching family goals
                  </h3>
                </div>
                <p className={classNames.productItemDescription}>
                  Set expectations around the number of cycles needed to meet
                  family goals with IVF.
                </p>
              </div>
              <div className={classNames.productItem}>
                <div className={classNames.productIconContainer}>
                  <IconUserScan className={classNames.productIcon} />
                  <h3 className={classNames.productItemTitle}>
                    Individual insights
                  </h3>
                </div>
                <p className={classNames.productItemDescription}>
                  Provide patients personalized information about the factors
                  that impact their chance of success.
                </p>
              </div>
            </div>
            <div className={classNames.productImgContainer}>
              <img
                className={['tw-flex', 'tw-object-contain'].join(' ')}
                src={productImg}
              />
            </div>
          </div>
        </Section>
        <BottomCta
          bgClassName={bottomCta.background}
          title="Take your patient consultations to the next level with personalized predictions"
          subtitle="Try Success Predictor today! Get access to a one month free trial, no EMR connection needed."
          ctaText="Request a demo"
          ctaLink="/success-predictor/request-demo"
          ctaCallback={() => requestDemoTrack('SP Landing Page Header')}
        />
        <Footer />
      </div>
    </div>
  );
};
const classNames = {
  heroContainer: [
    'tw-flex',
    'tw-flex',
    'tw-self-stretch',
    'tw-justify-center',
    'tw-bg-periwinkle-100',
  ].join(' '),
  hero: [
    'tw-flex',
    'tw-flex-col',
    'tw-py-[32px]',
    'md:tw-py-[80px]',
    'lg::tw-py-0',
    'lg::tw-pt-[72px]',
    'lg:tw-pb-[120px]',
    'tw-mx-[16px]',
    'md:tw-mx-[80px]',
  ].join(' '),
  heroChildren: [
    'tw-flex',
    'tw-flex-1',
    'tw-justify-center',
    'tw-mx-[28px]',
    'sm:tw-mx-[78px]',
    'lg:tw-mx-0',
  ].join(' '),
  heroImage: [
    'tw-flex',
    'tw-object-contain',
    'md:tw-max-w-[452px]',
    'lg:tw-max-w-[553px]',
  ].join(' '),
  howItWorks: [
    'tw-flex',
    'tw-flex-col',
    'lg:tw-flex-row',
    'tw-gap-[16px]',
    'md:tw-gap-[24px]',
    'tw-items-center',
  ].join(' '),
  howItWorksContainer: [
    'tw-flex',
    'tw-flex-1',
    'tw-flex-col',
    'tw-gap-[16px]',
    'md:tw-gap-[24px]',
  ].join(' '),
  howItWorksEyebrow: ['tw-button', 'tw-text-white'].join(' '),
  howItWorksTitle: ['tw-display3', 'tw-text-white'].join(' '),
  howItWorksBullet: ['tw-h3', 'tw-text-white', 'tw-ml-[1em]'].join(' '),
  productSection: [
    'tw-flex',
    'tw-flex-col',
    'tw-gap-[36px]',
    'md:tw-gap-[48px]',
  ].join(' '),
  productTitle: ['tw-flex', 'tw-flex-col', 'tw-gap-[24px]'].join(' '),
  productContentContainer: [
    'tw-flex',
    'tw-flex-col-reverse',
    'lg:tw-flex-row',
    'tw-gap-[36px]',
    'md:tw-gap-[48px]',
  ].join(' '),
  productContent: [
    'tw-flex-1',
    'tw-grid',
    'md:tw-grid-cols-2',
    'lg:tw-grid-cols-1',
    'lg:tw-max-w-[565px]',
    'lg:tw-content-between',
    'tw-gap-[24px]',
  ].join(' '),
  productItem: [
    'tw-flex',
    'tw-flex-col',
    'tw-items-start',
    'tw-gap-[8px]',
  ].join(' '),
  productIconContainer: [
    'tw-flex',
    'tw-flex-col',
    'xl:tw-flex-row',
    'tw-gap-[8px]',
  ].join(' '),
  productIcon: ['tw-h-[30px]', 'tw-w-[30px]', 'tw-text-periwinkle-700'].join(
    ' ',
  ),
  productItemTitle: 'tw-h3-emphasis',
  productItemDescription: 'tw-body',
  productImgContainer: [
    'tw-flex',
    'tw-flex-1',
    'tw-self-center',
    'tw-max-w-[537px]',
    'lg:tw-max-w-[454px]',
  ].join(' '),
};
export default SuccessPredictor;
